/**
 * Cursor-pagination helpers for the `{ items, next_cursor }` keyset envelope.
 *
 * CLAUDE-A already owns the core pieces — `Page<T>` + limit clamping in
 * `@/lib/api/http`, and `useCursorQuery` / `toListParams` in
 * `@/components/data-table`. This module only adds the missing glue:
 * a filter-aware page fetcher over `apiFetch`, and `useCursorList`, a thin
 * alias over `useCursorQuery` so feature code has one import site
 * (`@/features/shared/ops-api`) for the whole cursor story. Duplicate nothing.
 */
import type { QueryKey } from "@tanstack/react-query";

import {
  apiFetchOperational as apiFetch,
  type Page,
  type QueryParams,
  type QueryValue,
} from "@/lib/api/http";
import {
  toListParams,
  useCursorQuery,
  type CursorQueryArgs,
  type CursorQueryResult,
  type UseCursorQueryOptions,
} from "@/components/data-table";

// Re-exports so features never reach around this module for cursor plumbing.
export { MAX_PAGE_LIMIT, type Page } from "@/lib/api/http";
export {
  CursorTraversalError,
  DEFAULT_PAGE_LIMIT,
  MAX_CURSOR_ITEMS,
  MAX_CURSOR_PAGES,
  collectCursorPages,
  toListParams,
  useCursorQuery,
  type CursorPage,
  type CursorQueryArgs,
  type CursorQueryResult,
  type CollectCursorPagesOptions,
  type CursorPageLike,
  type SortState,
  type UseCursorQueryOptions,
} from "@/components/data-table";

/**
 * Extra query-string filters for a list endpoint (e.g. `{ status: "open" }`).
 * `undefined`/`null` values are omitted by `buildQuery`. Reserved paging keys
 * (`cursor`, `limit`, `sort`) are always taken from the paging args, never
 * from filters.
 */
export type ListFilters = Record<string, QueryValue>;

export interface FetchPageArgs extends CursorQueryArgs {
  filters?: ListFilters;
  signal?: AbortSignal;
}

/**
 * Fetch one page of a cursor-paginated list via CLAUDE-A's http wrapper.
 * `limit` defaults to 50 and is clamped to the 200 server cap; `cursor` is
 * omitted on the first page; `sort` serializes as `sort=<key>:<dir>`.
 */
export function fetchPage<T>(path: string, args: FetchPageArgs = {}): Promise<Page<T>> {
  const { filters, signal, ...paging } = args;
  // Paging params spread last so filters can never clobber cursor/limit/sort.
  const query: QueryParams = { ...filters, ...toListParams(paging) };
  return apiFetch<Page<T>>(path, { query, signal });
}

/**
 * Bind a path + fixed filters into a `useCursorList`/`useCursorQuery` fetcher.
 * The query key MUST include the same filters (and sort/limit) — a filter
 * change must start a fresh page sequence, not append to the old one.
 */
export function pageFetcher<T>(
  path: string,
  filters?: ListFilters,
): (args: CursorQueryArgs) => Promise<Page<T>> {
  return (args) => fetchPage<T>(path, { ...args, filters });
}

/**
 * Cursor list hook for feature `queries.ts` files: `useInfiniteQuery` over the
 * `{ items, next_cursor }` envelope with a flat `rows` selector,
 * `getNextPageParam` from `next_cursor` (null = last page), and first-load /
 * error flags shaped for `DataTable`.
 *
 * This IS CLAUDE-A's `useCursorQuery` (re-exported under the name the ops
 * specs use) — one implementation, two names.
 *
 * ```ts
 * const orders = useCursorList(
 *   queryKeys.orders.list({ status, sort: sort?.key, dir: sort?.dir }),
 *   pageFetcher<Order>("/orders", { status }),
 *   { sort },
 * );
 * ```
 */
export function useCursorList<T>(
  queryKey: QueryKey,
  fetcher: (args: CursorQueryArgs) => Promise<Page<T>>,
  options: UseCursorQueryOptions = {},
): CursorQueryResult<T> {
  return useCursorQuery(queryKey, fetcher, options);
}
